import type { ActedForView, QuestionKind } from '@myrewards/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type ReactElement } from 'react';
import {
  Dialog,
  FieldError,
  Form,
  Heading,
  Label,
  Modal,
  ModalOverlay,
  RadioButton,
  RadioField,
  RadioGroup,
  Text,
  TextArea,
  TextField,
} from 'react-aria-components';
import { isApiError } from '../api';
import { actingForKeys, questionActedFor } from '../api/actingFor';
import { Button, cx, Icon } from '../ui';
import {
  questionAction,
  questionEyebrow,
  questionIntro,
  QUESTION_KINDS,
  questionReaders,
  whatHappensNext,
} from './doneWords';
import { actForProblemWords, refusalRefreshes } from './words';
import a from './ActingFor.module.css';
import q from './QuestionDialog.module.css';
import d from '../ui/ConfirmDialog.module.css';

// APP-148 D6, D17 (the Question board): the person a Stock Admin acted for says it isn't what
// they asked. What's wrong and their words are both required. While the action left the award
// going on, sending it holds the award before its next step; the System Admins and the Stock Admin
// team are told either way. The server re-checks everything (§Security 6).

const KIND_MISSING = 'Choose what’s wrong.';
const WORDS_MISSING = 'Tell us what you asked for, in a few words.';

/** Where the dialog opened, so a row asked about already says where it shows (R117). */
export type QuestionPlace = 'done_for_you' | 'record';

/** What the dialog says when another tab or a reload asked first, where the reader is. */
const ASKED_ALREADY: Readonly<Record<QuestionPlace, string>> = {
  done_for_you: 'This was asked about already. Its row under Done for you says where it stands.',
  record: 'This was asked about already. It shows as asked here and under Done for you.',
};

export interface QuestionDialogProps {
  /** The row asked about, while the dialog is open; null when it's closed. */
  view: ActedForView | null;
  onClose: () => void;
  /** The row as it is once asked (held, or told without a hold). */
  onAsked: (view: ActedForView) => void;
  /** Where it opened: Done for you (Home's card and the page) by default, or the award record. */
  where?: QuestionPlace;
}

export function QuestionDialog({
  view,
  onClose,
  onAsked,
  where = 'done_for_you',
}: QuestionDialogProps): ReactElement {
  // While it's being sent, the dialog can't be dismissed: the answer says whether it was held.
  const [pending, setPending] = useState(false);
  return (
    <ModalOverlay
      isOpen={view !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      isDismissable={!pending}
      isKeyboardDismissDisabled={pending}
      className={d.overlay}
    >
      <Modal className={cx(d.modal, d.modalWide)}>
        {/* Keyed by the row: each opening starts afresh. */}
        {view !== null && (
          <QuestionForm
            key={view.id}
            view={view}
            pending={pending}
            setPending={setPending}
            onClose={onClose}
            onAsked={onAsked}
            where={where}
          />
        )}
      </Modal>
    </ModalOverlay>
  );
}

function QuestionForm({
  view,
  pending,
  setPending,
  onClose,
  onAsked,
  where,
}: {
  view: ActedForView;
  pending: boolean;
  setPending: (pending: boolean) => void;
  onClose: () => void;
  onAsked: (view: ActedForView) => void;
  where: QuestionPlace;
}): ReactElement {
  const [kind, setKind] = useState<QuestionKind | null>(null);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Whether a send was tried: only then do the fields say what's missing.
  const [tried, setTried] = useState(false);
  const kindRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const introId = useId();
  const queryClient = useQueryClient();
  // Refused as asked already (another tab, or a reload behind it): nothing is left to send.
  const [askedAlready, setAskedAlready] = useState(false);
  const asking = askedAlready ? null : view.asking;

  if (asking === null)
    // Asked already, or not theirs to ask: the row says where it stands; nothing to send.
    return (
      <Dialog className={d.dialog}>
        <Heading slot="title" className={cx('disp', d.title)}>
          Not what you asked?
        </Heading>
        <p className={d.intro}>{ASKED_ALREADY[where]}</p>
        <div className={d.actions}>
          <Button variant="navy" onPress={onClose}>
            Close
          </Button>
        </div>
      </Dialog>
    );

  return (
    <Dialog className={d.dialog} aria-describedby={introId}>
      <p className={cx('eyebrow', q.eyebrow)}>{questionEyebrow(view)}</p>
      <Heading slot="title" className={cx('disp', d.title)}>
        Not what you asked?
      </Heading>
      <p id={introId} className={d.intro}>
        {questionIntro(asking, view.for.role)}
      </p>
      <Form
        className={q.form}
        // The fields say what's missing once a send was tried, and stop saying it as soon as
        // it's there (no wait for the field to lose focus).
        validationBehavior="aria"
        onSubmit={(event) => {
          event.preventDefault();
          if (pending) return;
          setTried(true);
          if (kind === null) {
            kindRef.current?.querySelector('input')?.focus();
            return;
          }
          if (text.trim() === '') {
            textRef.current?.focus();
            return;
          }
          setPending(true);
          setError(null);
          void questionActedFor(view.id, { kind, text: text.trim() })
            .then((asked) => {
              onAsked(asked);
            })
            .catch((failure: unknown) => {
              // The facts moved on (asked already, the award moved, a switch): read the rows
              // again, so the page behind says where they stand now.
              if (refusalRefreshes(failure))
                void queryClient.invalidateQueries({ queryKey: actingForKeys.all });
              if (isApiError(failure) && failure.status === 409) {
                setAskedAlready(true);
                return;
              }
              // The server's own words: a field's message (R43), else why it was refused.
              setError(actForProblemWords(failure));
            })
            .finally(() => {
              setPending(false);
            });
        }}
      >
        {error !== null && (
          <p className={d.error} role="alert">
            {error}
          </p>
        )}
        <RadioGroup
          className={a.section}
          value={kind}
          onChange={(value) => {
            setKind(value as QuestionKind);
          }}
          ref={kindRef}
          isRequired
          isDisabled={pending}
          isInvalid={tried && kind === null}
        >
          <Label className={a.fieldLabel}>
            What’s wrong? <span className={a.required}>Required</span>
          </Label>
          <div className={q.kinds}>
            {QUESTION_KINDS.map((k) => (
              <RadioField key={k.id} value={k.id}>
                <RadioButton className={a.personTile}>
                  <span className={a.personText}>
                    <span className={a.personName}>{k.label}</span>
                    <span className={a.personTitle}>{k.hint}</span>
                  </span>
                  <span className={a.radioMark} aria-hidden="true" />
                </RadioButton>
              </RadioField>
            ))}
          </div>
          <FieldError className={q.fieldError}>{KIND_MISSING}</FieldError>
        </RadioGroup>

        <TextField
          className={a.section}
          value={text}
          onChange={setText}
          isRequired
          isDisabled={pending}
          isInvalid={tried && text.trim() === ''}
        >
          <Label className={a.fieldLabel}>
            Tell us more <span className={a.required}>Required</span>
          </Label>
          <TextArea ref={textRef} className={a.textarea} maxLength={2000} />
          <Text slot="description" className={a.hint}>
            {questionReaders(view)}
          </Text>
          <FieldError className={q.fieldError}>{WORDS_MISSING}</FieldError>
        </TextField>

        <div className={a.summary}>
          <p className={cx('eyebrow', a.summaryTitle)}>What happens next</p>
          <ul className={q.next}>
            {whatHappensNext(view, asking).map((line) => (
              <li key={line.text}>
                <span className={q.nextIcon} aria-hidden="true">
                  <Icon name={line.icon} size={16} />
                </span>
                <span>{line.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={q.foot}>
          <p className={q.footNote}>
            {/* A budget or setting change has no award, so no award history to keep them in. */}
            {view.award === null
              ? 'Recorded in the audit log, with your words.'
              : 'Recorded in the award’s history and the audit log, with your words.'}
          </p>
          <div className={q.buttons}>
            <Button variant="ghost" onPress={onClose} isDisabled={pending}>
              Cancel
            </Button>
            <Button type="submit" variant="navy" isPending={pending}>
              {questionAction(asking)}
            </Button>
          </div>
        </div>
      </Form>
    </Dialog>
  );
}
